import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, Lightbulb, Loader2, MessageSquarePlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccount } from "@/contexts/account.context";
import { toast } from "@/hooks/use-toast";
import { isHeyApiError, tenantAtivo } from "@/lib/hey-api-client";
import { tituloDaTela } from "@/lib/titulos-das-telas";
import { cn } from "@/lib/utils";
import {
  MAXIMO_DA_MENSAGEM,
  MINIMO_DA_MENSAGEM,
  sendFeedback,
  type TipoDeFeedback,
} from "@/services/feedback.service";

/**
 * <M-281> O botão de feedback, presente em toda tela do painel.
 *
 * Abre um diálogo em que o usuário escolhe entre relatar um problema e sugerir uma melhoria
 * e escreve a mensagem. A tela (caminho e título) e a loja ativa vão junto, sem que ele
 * precise dizer onde estava. Quem lê é a equipe, pelo painel admin; o andamento não volta
 * para cá.
 *
 * Fica no canto inferior direito, acima da barra de navegação no mobile.
 */

const TIPOS: {
  valor: TipoDeFeedback;
  rotulo: string;
  icone: typeof Bug;
  dica: string;
}[] = [
  {
    valor: "bug",
    rotulo: "Relatar um problema",
    icone: Bug,
    dica: "O que aconteceu? O que você esperava que acontecesse?",
  },
  {
    valor: "improvement",
    rotulo: "Sugerir uma melhoria",
    icone: Lightbulb,
    dica: "O que poderia ser melhor nesta tela, e por quê?",
  },
];

export const FeedbackButton = () => {
  const location = useLocation();
  const { selectedAccount } = useAccount();
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState<TipoDeFeedback>("bug");
  const [mensagem, setMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);

  const caminho = `${location.pathname}${location.search}`;
  const titulo = tituloDaTela(location.pathname);
  const tamanho = mensagem.trim().length;
  const podeEnviar = tamanho >= MINIMO_DA_MENSAGEM && !enviando;
  const dica = TIPOS.find((item) => item.valor === tipo)?.dica ?? "";

  const enviar = async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    try {
      const recibo = await sendFeedback({
        kind: tipo,
        message: mensagem.trim(),
        page_path: caminho,
        page_title: titulo,
        customer_uuid: selectedAccount?.customer_uuid ?? tenantAtivo() ?? null,
      });
      toast({ title: "Feedback enviado", description: recibo.message });
      setMensagem("");
      setTipo("bug");
      setAberto(false);
    } catch (erro) {
      // O erro com resposta da API já virou toast no interceptor (`hey-api-client`); aqui
      // fica só o que não chegou a ter resposta. A mensagem continua no campo.
      if (!isHeyApiError(erro)) {
        toast({
          title: "Não foi possível enviar",
          description: "Verifique sua conexão e tente de novo.",
          variant: "destructive",
        });
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            aria-label="Enviar feedback"
            onClick={() => setAberto(true)}
            className="fixed bottom-20 right-4 z-40 h-11 w-11 rounded-full shadow-lg lg:bottom-6 lg:right-6"
          >
            <MessageSquarePlus className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">Enviar feedback</TooltipContent>
      </Tooltip>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Enviar feedback</DialogTitle>
            <DialogDescription>
              Encontrou um problema ou tem uma ideia? Conte para a nossa equipe.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div
              role="radiogroup"
              aria-label="Tipo de feedback"
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
              {TIPOS.map(({ valor, rotulo, icone: Icone }) => (
                <button
                  key={valor}
                  type="button"
                  role="radio"
                  aria-checked={tipo === valor}
                  onClick={() => setTipo(valor)}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                    tipo === valor
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-input text-muted-foreground hover:bg-muted",
                  )}
                >
                  <Icone className="h-4 w-4 shrink-0" />
                  {rotulo}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-mensagem">Mensagem</Label>
              <Textarea
                id="feedback-mensagem"
                value={mensagem}
                onChange={(evento) => setMensagem(evento.target.value)}
                placeholder={dica}
                maxLength={MAXIMO_DA_MENSAGEM}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                {tamanho < MINIMO_DA_MENSAGEM
                  ? `Escreva pelo menos ${MINIMO_DA_MENSAGEM} caracteres.`
                  : `${tamanho} de ${MAXIMO_DA_MENSAGEM} caracteres.`}
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              Enviaremos junto a tela em que você está:{" "}
              <span className="font-medium text-foreground">
                {titulo ?? location.pathname}
              </span>
              {selectedAccount ? ` · loja ${selectedAccount.name}` : ""}.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAberto(false)}
              disabled={enviando}
            >
              Cancelar
            </Button>
            <Button type="button" onClick={enviar} disabled={!podeEnviar}>
              {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
